import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { gsap } from "gsap";
import PrimaryButton from "../../ui/PrimaryButton";
import SecondaryButton from "../../ui/SecondaryButton";

const HERO_VISUALS = [
  "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1523240794102-9c1f44ec4f84?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1462530260150-162092dbf011?auto=format&fit=crop&w=1400&q=80"
];

const hashText = (value = "") =>
  value.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);

const pickVisual = (seedText, list) => list[hashText(seedText) % list.length];

function PageHeroSplit({
  badge,
  title,
  description,
  highlights = [],
  primaryCta,
  secondaryCta,
  sideTitle = "",
  sideDescription = "",
  sideItems = [],
  heroImage
}) {
  const contentRef = useRef(null);
  const sideRef = useRef(null);

  const fallbackVisual = useMemo(
    () => pickVisual(`${title || ""}${sideTitle || ""}`, HERO_VISUALS),
    [title, sideTitle]
  );
  const preferredVisual = heroImage || fallbackVisual;
  const [currentImage, setCurrentImage] = useState(preferredVisual);

  useEffect(() => {
    setCurrentImage(preferredVisual);
  }, [preferredVisual]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" }
      );
      if (sideRef.current) {
        gsap.fromTo(
          sideRef.current,
          { opacity: 0, y: 34, scale: 0.98 },
          { opacity: 1, y: 0, scale: 1, duration: 0.75, delay: 0.14, ease: "power3.out" }
        );
      }
    });
    return () => ctx.revert();
  }, []);

  return (
    <section className="relative overflow-hidden bg-white pb-24 pt-40 sm:pt-44 lg:pt-48">
      <div className="absolute inset-0 opacity-70">
        <div
          className="h-full w-full"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(116,119,127,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(116,119,127,0.08) 1px, transparent 1px)",
            backgroundSize: "60px 60px"
          }}
        />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
          <div ref={contentRef} style={{ opacity: 0 }}>
            {badge ? (
              <p className="inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-gold-strong">
                <span className="h-px w-10 bg-brand-gold-strong/70" />
                {badge}
              </p>
            ) : null}

            <h1 className="mt-7 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-tight text-brand-navy sm:text-6xl lg:text-[4.2rem]">
              {title}
            </h1>

            <p className="mt-7 max-w-2xl text-base leading-relaxed text-brand-charcoal/76 sm:text-lg">
              {description}
            </p>

            {(primaryCta || secondaryCta) ? (
              <div className="mt-9 flex flex-wrap gap-4">
                {primaryCta ? (
                  <PrimaryButton showArrow to={primaryCta.to} href={primaryCta.href}>
                    {primaryCta.label}
                  </PrimaryButton>
                ) : null}
                {secondaryCta ? (
                  <SecondaryButton
                    className="border-brand-line bg-white text-brand-navy hover:border-brand-gold/55 hover:bg-brand-gold-soft/35 hover:text-brand-navy"
                    to={secondaryCta.to}
                    href={secondaryCta.href}
                  >
                    {secondaryCta.label}
                  </SecondaryButton>
                ) : null}
              </div>
            ) : null}

            {highlights.length > 0 ? (
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {highlights.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm text-brand-charcoal/78">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-gold-strong" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div ref={sideRef} style={{ opacity: 0 }} className="relative mx-auto w-full max-w-md lg:mx-0">
            <div className="absolute -right-6 -top-6 h-full w-full border-t-2 border-r-2 border-brand-gold/25" />
            <div className="relative z-10 overflow-hidden border-r-[8px] border-b-[8px] border-brand-gold-strong shadow-[0_34px_60px_-30px_rgba(10,31,68,0.45)]">
              <div className="relative h-[420px] sm:h-[460px]">
                <img
                  src={currentImage}
                  alt={sideTitle || title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={() => {
                    if (currentImage !== fallbackVisual) {
                      setCurrentImage(fallbackVisual);
                    }
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#061630]/86 via-[#061630]/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7">
                  {sideTitle ? <p className="text-2xl font-semibold leading-tight text-white">{sideTitle}</p> : null}
                  {sideDescription ? (
                    <p className="mt-3 text-sm leading-relaxed text-white/95">{sideDescription}</p>
                  ) : null}
                </div>
              </div>
            </div>

            {sideItems.length > 0 ? (
              <ol className="mt-6 space-y-3">
                {sideItems.map((item, index) => (
                  <li key={item} className="flex items-start gap-3 border-l border-brand-gold-strong/60 pl-3.5 text-sm text-brand-charcoal/78">
                    <span className="mt-0.5 text-xs font-semibold text-brand-gold-strong">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ol>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export default PageHeroSplit;
